/**
 * workspaceLabels.js
 *
 * Localized labels of the workspace dashboard CATALOGUE (widget titles, KPI /
 * series names, status terms, action sentences, module names).
 *
 * They are the admin's `mainDashboard` strings (en/hu/de/cn/ro copied from the
 * admin locale modules, svn translated) and live in
 * `config/workspaceLabels/<lang>.json`, NOT in `locales/*.json`: ~420 catalogue
 * strings x 6 languages would add ~20% to every locale catalog that the rest of
 * the app loads, for text only this screen reads. All six files carry exactly the
 * same keys (pinned by `workspaceLabels.test.js`) - the usual all-or-nothing rule.
 *
 * The UI chrome (tabs, edit mode, sheets, errors) uses ordinary `wsd_*` keys in
 * `locales/*.json`.
 *
 * @module helpers/workspaces/workspaceLabels
 */

/* eslint-disable global-require */
const LOADERS = {
    en: () => require('../../config/workspaceLabels/en.json'),
    hu: () => require('../../config/workspaceLabels/hu.json'),
    de: () => require('../../config/workspaceLabels/de.json'),
    cn: () => require('../../config/workspaceLabels/cn.json'),
    ro: () => require('../../config/workspaceLabels/ro.json'),
    svn: () => require('../../config/workspaceLabels/svn.json'),
};
/* eslint-enable global-require */

/** Languages with a label file (the six app locales). */
export const LABEL_LANGUAGES = Object.keys(LOADERS);

/** BCP-47 tag per app language, for number / date formatting. */
export const LOCALE_TAGS = {en: 'en-US', hu: 'hu-HU', de: 'de-DE', cn: 'zh-CN', ro: 'ro-RO', svn: 'sl-SI'};

const cache = {};

/**
 * Resolve an i18n language code to one of the six label languages.
 *
 * @param {?string} language i18n language (`hu`, `en-US`, `svn`, ...).
 * @returns {string} A key of {@link LABEL_LANGUAGES} (`hu` - the app default - when unknown).
 */
export const resolveLabelLanguage = (language) => {
    const code = String(language || '').toLowerCase();

    if (LOADERS[code]) {
        return code;
    }

    const base = code.split(/[-_]/)[0];

    if (base === 'zh') {
        return 'cn';
    }

    if (base === 'sl') {
        return 'svn';
    }

    return LOADERS[base] ? base : 'hu';
};

/**
 * @param {?string} language i18n language.
 * @returns {Object.<string, string>} The flat label map (cached).
 */
export const getLabels = (language) => {
    const lang = resolveLabelLanguage(language);

    if (!cache[lang]) {
        cache[lang] = LOADERS[lang]();
    }

    return cache[lang];
};

/**
 * Replace `{{name}}` placeholders.
 *
 * @param {string} text Template.
 * @param {object} [params] Values.
 * @returns {string} Interpolated text.
 */
export const interpolate = (text, params = {}) => String(text ?? '').replace(
    /\{\{\s*(\w+)\s*\}\}/g,
    (match, key) => (params[key] === undefined || params[key] === null ? '' : String(params[key])),
);

/**
 * Look a label up with an English fallback and an optional literal fallback.
 *
 * @param {?string} language i18n language.
 * @param {string} key Label key (`w.<id>`, `kpi.<module>.<kpi>`, ...).
 * @param {string} [fallback] Used when neither the language nor English has the key.
 * @param {object} [params] Interpolation values.
 * @returns {string} The label.
 */
export const label = (language, key, fallback, params) => {
    const value = getLabels(language)[key] ?? getLabels('en')[key] ?? fallback ?? key;

    return params ? interpolate(value, params) : value;
};

/**
 * Humanize a raw code (`in_progress` -> `In progress`) for unknown terms.
 *
 * @param {*} key Raw code.
 * @returns {string} Readable text.
 */
export const humanizeKey = (key) => String(key ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());

/**
 * Localize a server status / breakdown code through the shared `term.*` vocabulary
 * (same normalisation as the admin's `termLabel`); unknown terms keep the server text.
 *
 * @param {?string} language i18n language.
 * @param {*} key Raw code (`in_progress`, `APPROVED`, `slaBreached`).
 * @param {string} [fallback] Server label used when there is no translation.
 * @returns {string} Localized term.
 */
export const termLabel = (language, key, fallback) => {
    const raw = String(key ?? '');
    const base = /^[A-Z0-9_ -]+$/.test(raw) ? raw.toLowerCase() : raw;
    const camel = base.replace(/[_\s-]+([a-zA-Z0-9])/g, (match, c) => c.toUpperCase());
    const safe = camel.replace(/[^A-Za-z0-9]/g, '');

    if (!safe) {
        return fallback || raw;
    }

    return label(language, `term.${safe}`, fallback || humanizeKey(key));
};

/**
 * Title of a widget instance.
 *
 * @param {?string} language i18n language.
 * @param {string} type Catalogue id.
 * @returns {string} Localized title (humanized id for an unknown widget).
 */
export const widgetTitle = (language, type) => label(language, `w.${type}`, humanizeKey(type));

/**
 * Localized KPI name.
 *
 * @param {?string} language i18n language.
 * @param {string} module Module key.
 * @param {string} kpi KPI key.
 * @returns {string} Name.
 */
export const kpiLabel = (language, module, kpi) => label(language, `kpi.${module}.${kpi}`, humanizeKey(kpi));

/**
 * Localized series name.
 *
 * @param {?string} language i18n language.
 * @param {string} module Module key.
 * @param {string} series Series key.
 * @returns {string} Name.
 */
export const seriesLabel = (language, module, series) => label(language, `series.${module}.${series}`, humanizeKey(series));

/**
 * One-line sentence of an action item (`action.<module>.<type>` with the
 * server's English `defaultTitle` as fallback).
 *
 * @param {?string} language i18n language.
 * @param {object} action Action from `summary.actions.items`.
 * @param {(amount: number, currency: ?string) => string} [formatAmount] Money formatter for `{{amount}}`.
 * @returns {string} Sentence.
 */
export const actionTitle = (language, action, formatAmount) => {
    const amount = action?.params?.amount ?? action?.amount;
    const params = {
        ...(action?.params || {}),
        count: action?.params?.count ?? action?.count,
        amount: formatAmount && typeof amount === 'number' ? formatAmount(amount, action?.params?.currency || action?.currency) : amount,
    };
    const key = `action.${action?.moduleKey}.${action?.type}`;

    return label(language, key, interpolate(action?.defaultTitle || humanizeKey(action?.type), params), params);
};
